/**
 * Figma "Team & roles": people, invitations and what each role can do — served by Identity; the
 * portal is only the UI. Every action is enforced again by Identity (no-escalation, last Owner).
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Crown, Search, Users, X } from "lucide-react";
import { useCallback, useState, type ReactNode } from "react";

import { HelpHint } from "@/components/help";
import { HELP_KEYS } from "@/lib/help-context";
import { useOrganization } from "@/auth/organization";
import { useUser } from "@/auth/session";
import {
  InviteDialog,
  OnlyOwnerDialog,
  RemoveMemberDialog,
  TransferOwnershipDialog,
} from "@/components/team/dialogs";
import { useAccessResources } from "@/components/team/AccessEditor";
import { ManageMemberDialog } from "@/components/team/ManageMemberDialog";
import { customerRoles, describeRole, isOwner, shortName, splitRoles } from "@/components/team/roles";
import { RolesMatrix } from "@/components/team/RolesMatrix";
import { Avatar, InviteAvatar, RolePill } from "@/components/team/ui";
import {
  Button,
  CopyLinkButton,
  cx,
  ErrorAlert,
  ErrorState,
  FullPageSpinner,
  Input,
  Pager,
  Select,
  Spinner,
  useCursorPager,
  usePrefetchNext,
  useToast,
} from "@/components/ui";
import { AUTO_REFRESH_MS, DataFreshness, freshnessOf } from "@/components/ui/DataFreshness";
import { missingPermissionReason, restrictionSummary, type ResourceNames } from "@/lib/access";
import { identity } from "@/lib/api";
import { timeAgo } from "@/lib/format";
import type { Invitation, Member, Role, RoleRestrictions } from "@/lib/types";
import { choice, text, useUrlText, useUrlView } from "@/lib/url-state";

const TABS = [
  ["people", "People"],
  ["roles", "Roles & permissions"],
] as const;
type Tab = (typeof TABS)[number][0];

/**
 * The view kept in the URL (PHP-3341): `?tab=` (default people), and on People `?q=` (search, default
 * none) and `?role=` (a role id, default all roles). People are listed in Identity's order (joined first).
 */
const VIEW = {
  tab: choice(
    "tab",
    TABS.map(([t]) => t),
    "people",
  ),
  q: text("q"),
  role: text("role"),
};

type Dialog =
  | { kind: "invite"; owner?: boolean }
  | { kind: "manage"; member: Member }
  | { kind: "remove"; member: Member }
  | { kind: "only-owner" }
  | { kind: "transfer" };

const panel = "rounded-[14px] border border-border bg-surface";

export function TeamPage() {
  const me = useUser();
  const { orgId, current, can } = useOrganization();
  const qc = useQueryClient();
  const [view, setView] = useUrlView(VIEW);
  const tab: Tab = view.tab;
  // Switching tabs keeps the People filters, so coming back shows the same list.
  const setTab = (t: Tab) => setView({ tab: t }, { replace: true });
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const commitQuery = useCallback((q: string) => setView({ q }, { replace: true }), [setView]);
  const [query, setQuery] = useUrlText(view.q, commitQuery);

  // Every member, for what needs them all: counts, who owns the business, roles in use, names. The list below
  // is paged and searched on the server.
  const members = useQuery({
    queryKey: ["members", orgId],
    queryFn: () => identity.members(orgId),
    refetchInterval: AUTO_REFRESH_MS,
  });
  const roles = useQuery({
    queryKey: ["roles", orgId],
    queryFn: () => identity.roles(orgId),
    refetchInterval: AUTO_REFRESH_MS,
  });
  // The list filters come from the URL; the search there is already debounced by useUrlText. A link to a role
  // that's gone (or never was) shows everyone, so the list waits for the roles to know.
  const search = view.q.trim();
  const roleFilter = roles.data && customerRoles(roles.data).some((r) => r.id === view.role) ? view.role : "";
  const listKey = `${search}|${roleFilter}`;
  const peoplePager = useCursorPager(listKey);
  const invitePager = useCursorPager(listKey);
  const peopleKey = (token: string | null) => [
    "members",
    orgId,
    "page",
    { q: search, role: roleFilter, size: peoplePager.pageSize, token },
  ];
  const fetchPeople = (token: string | null) =>
    identity.membersPage(orgId, {
      q: search,
      roleId: roleFilter || undefined,
      pageSize: peoplePager.pageSize,
      pageToken: token,
    });
  const peoplePage = useQuery({
    queryKey: peopleKey(peoplePager.token),
    queryFn: () => fetchPeople(peoplePager.token),
    enabled: roles.isSuccess,
    placeholderData: keepPreviousData,
    refetchInterval: AUTO_REFRESH_MS,
  });
  usePrefetchNext(peoplePage.data?.next_page_token, peopleKey, fetchPeople);
  const inviteKey = (token: string | null) => [
    "invitations",
    orgId,
    "page",
    { q: search, role: roleFilter, size: invitePager.pageSize, token },
  ];
  const fetchInvites = (token: string | null) =>
    identity.invitationsPage(orgId, {
      q: search,
      roleId: roleFilter || undefined,
      pageSize: invitePager.pageSize,
      pageToken: token,
    });
  const invitePage = useQuery({
    queryKey: inviteKey(invitePager.token),
    queryFn: () => fetchInvites(invitePager.token),
    enabled: roles.isSuccess,
    placeholderData: keepPreviousData,
    refetchInterval: AUTO_REFRESH_MS,
  });
  usePrefetchNext(invitePage.data?.next_page_token, inviteKey, fetchInvites);
  const permissionCatalogue = useQuery({
    queryKey: ["permission-catalogue"],
    queryFn: identity.permissionCatalogue,
    staleTime: 5 * 60_000,
  });
  const invitations = useQuery({
    queryKey: ["invitations", orgId],
    queryFn: () => identity.invitations(orgId),
    refetchInterval: AUTO_REFRESH_MS,
  });
  const toast = useToast();
  const resend = useMutation({
    mutationFn: (id: string) => identity.resendInvitation(orgId, id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["invitations", orgId] });
      toast.show({ tone: "success", title: "Invitation sent again" });
    },
  });
  const revoke = useMutation({
    mutationFn: (id: string) => identity.revokeInvitation(orgId, id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["invitations", orgId] });
      toast.show({ tone: "success", title: "Invitation cancelled" });
    },
  });
  // Website and folder names, only needed once someone's role is narrowed.
  const narrowed =
    (members.data?.items ?? []).some((m) => m.roles.some((r) => r.restrictions)) ||
    (invitations.data ?? []).some((i) => i.restrictions && Object.keys(i.restrictions).length > 0);
  const { names } = useAccessResources(narrowed);

  if (members.isPending || roles.isPending) return <FullPageSpinner />;
  if (members.isError) return <ErrorState error={members.error} onRetry={() => void members.refetch()} />;
  if (roles.isError) return <ErrorState error={roles.error} onRetry={() => void roles.refetch()} />;
  if (tab === "roles" && permissionCatalogue.isPending) return <FullPageSpinner />;
  if (tab === "roles" && permissionCatalogue.isError)
    return (
      <ErrorState error={permissionCatalogue.error} onRetry={() => void permissionCatalogue.refetch()} />
    );

  const allRoles = customerRoles(roles.data);
  const roleById = new Map(allRoles.map((r) => [r.id, r]));
  const people = members.data.items;
  const pending = (invitations.data ?? []).filter((i) => i.status === "pending");
  const transfer = pending.find((i) => i.ownership_transfer);
  const invites = pending.filter((i) => !i.ownership_transfer);
  const mine = people.find((m) => m.user.id === me.id);
  const iAmOwner = !!mine && isOwner(mine);
  const owners = people.filter(isOwner).length;
  const canInvite = can("members:invite");
  const canManageMembers = can("members:manage_roles") || can("members:remove");

  const manageable = (m: Member) => m.user.id === me.id || (canManageMembers && (!isOwner(m) || iAmOwner));
  /** Why someone can't be managed: the role to ask for, or that only an Owner manages an Owner. */
  const notManageable = canManageMembers
    ? "Only an Owner can manage another Owner."
    : missingPermissionReason("members:manage_roles");
  const openManage = (m: Member) =>
    setDialog(
      m.user.id === me.id && iAmOwner && owners === 1
        ? { kind: "only-owner" }
        : { kind: "manage", member: m },
    );

  // The page on screen. A pending ownership offer shows in its banner, not in the list.
  const shownPeople = peoplePage.data?.items ?? [];
  const shownInvites = (invitePage.data?.items ?? []).filter((i) => !i.ownership_transfer);
  const filtering = search !== "" || roleFilter !== "";

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3.5">
          <span className="flex size-11 shrink-0 items-center justify-center rounded-xl border border-border bg-brand-50 text-fg-muted">
            <Users className="size-5" aria-hidden />
          </span>
          <div className="min-w-0">
            <h1 className="text-[26px] font-bold text-fg">Team & roles</h1>
            <p className="mt-1 text-sm text-fg-subtle">
              {people.length} {people.length === 1 ? "person" : "people"}
              {invites.length > 0 && ` · ${invites.length} invited`} · roles cover all of{" "}
              {current.organization.name} unless narrowed to specific websites or domain folders
            </p>
            <DataFreshness
              className="mt-1"
              {...freshnessOf(peoplePage, invitePage, members, roles, invitations)}
            />
          </div>
        </div>
        <Button
          className="rounded-full"
          requires="members:invite"
          onClick={() => setDialog({ kind: "invite" })}
        >
          Invite teammate
        </Button>
      </div>

      <div role="tablist" className="flex gap-1.5 self-start rounded-xl border border-border bg-brand-50 p-1">
        {TABS.map(([t, label]) => (
          <Button
            unstyled
            key={t}
            role="tab"
            type="button"
            aria-selected={tab === t}
            onClick={() => setTab(t)}
            className={cx(
              "flex items-center gap-2 rounded-[9px] px-3.5 py-2 text-[13.5px] font-semibold",
              tab === t
                ? "bg-surface text-fg shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
                : "text-fg-subtle hover:text-fg",
            )}
          >
            {label}
            <span
              className={cx(
                "rounded-full px-1.5 text-[11px]",
                tab === t ? "bg-[#fde0dd] text-[#b8291f]" : "bg-[#efecea] text-fg-subtle",
              )}
            >
              {t === "people" ? people.length + invites.length : allRoles.length}
            </span>
          </Button>
        ))}
      </div>

      {tab === "roles" ? (
        <RolesMatrix
          roles={roles.data}
          permissions={permissionCatalogue.data ?? []}
          myRoleKeys={mine?.roles.map((role) => role.key)}
        />
      ) : (
        <>
          {transfer && (
            <TransferBanner
              invitation={transfer}
              canAct={iAmOwner}
              busy={resend.isPending || revoke.isPending}
              onResend={() => resend.mutate(transfer.id)}
              onCancel={() => revoke.mutate(transfer.id)}
            />
          )}
          <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.95fr)]">
            <section className={cx(panel, "px-5 py-4")}>
              <div className="flex flex-wrap items-center gap-2 pb-3">
                <h2 className="mr-auto text-base font-semibold text-fg">Team members</h2>
                <label className="relative">
                  <span className="sr-only">Search people</span>
                  <Search className="pointer-events-none absolute top-1/2 left-3 size-3.5 -translate-y-1/2 text-fg-subtle" />
                  <Input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search people"
                    className="h-8 w-40 rounded-full py-1 pl-8 text-[13px]"
                  />
                </label>
                <Select
                  aria-label="Filter by role"
                  value={roleFilter}
                  onChange={(e) => setView({ role: e.target.value })}
                  className="h-8 w-auto rounded-full py-1 text-[13px]"
                >
                  <option value="">All roles</option>
                  {allRoles.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </Select>
                <CopyLinkButton />
              </div>
              <ErrorAlert error={resend.error ?? revoke.error} />
              {peoplePage.isError && (
                <div className="flex flex-col items-start gap-2 border-t border-border py-4">
                  <ErrorAlert error={peoplePage.error} />
                  <Button variant="secondary" size="sm" onClick={() => void peoplePage.refetch()}>
                    Try again
                  </Button>
                </div>
              )}
              {peoplePage.isPending && (
                <div className="flex justify-center border-t border-border py-8">
                  <Spinner />
                </div>
              )}
              <ul className="divide-y divide-border border-t border-border">
                {shownPeople.map((m) => {
                  const { main } = splitRoles(m);
                  const self = m.user.id === me.id;
                  const scope = scopeCaption(m.roles, names);
                  return (
                    <li key={m.user.id} className="flex items-center gap-3 py-3">
                      <Avatar name={m.user.display_name || m.user.email} roleKey={main?.key} />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-fg">
                          {shortName(m.user.display_name, m.user.email)}
                          {self && <span className="ml-1.5 font-normal text-fg-subtle">(you)</span>}
                        </p>
                        <p className="truncate text-xs text-fg-subtle">{m.user.email}</p>
                      </div>
                      <RoleWithScope roleKey={main?.key} scope={scope}>
                        {main?.name ?? "No role"}
                        {m.roles.length > 1 && ` +${m.roles.length - 1}`}
                      </RoleWithScope>
                      <Button
                        variant="secondary"
                        size="sm"
                        className="rounded-full"
                        disabledReason={manageable(m) ? undefined : notManageable}
                        onClick={() => openManage(m)}
                      >
                        Manage
                      </Button>
                    </li>
                  );
                })}
                {peoplePage.data && shownPeople.length === 0 && (
                  <li className="py-8 text-center text-sm text-fg-subtle">
                    {filtering ? "Nobody matches this search." : "No team members yet."}
                  </li>
                )}
              </ul>
              {peoplePage.data && (
                <Pager
                  className="border-t border-border pt-3"
                  pager={peoplePager}
                  shown={shownPeople.length}
                  total={peoplePage.data.total}
                  hasNext={!!peoplePage.data.next_page_token}
                  onNext={() =>
                    peoplePage.data?.next_page_token && peoplePager.next(peoplePage.data.next_page_token)
                  }
                  loading={peoplePage.isFetching}
                  noun="people"
                />
              )}
              {invitePage.data && invitePage.data.total > 0 && (
                <div className="mt-4 border-t border-border pt-3">
                  <h3 className="pb-1 text-[13px] font-semibold text-fg">
                    Pending invitations{" "}
                    <span className="font-normal text-fg-subtle">({invitePage.data.total})</span>
                  </h3>
                  <ul className="divide-y divide-border">
                    {shownInvites.map((i) => (
                      <InvitationRow
                        key={i.id}
                        invitation={i}
                        role={i.role_ids.map((id) => roleById.get(id)).find(Boolean)}
                        scope={scopeCaption(
                          i.role_ids.map((id) => ({
                            name: roleById.get(id)?.name ?? "Role",
                            restrictions: i.restrictions?.[id] ?? null,
                          })),
                          names,
                        )}
                        canAct={canInvite}
                        resending={resend.isPending && resend.variables === i.id}
                        onResend={() => resend.mutate(i.id)}
                        onRevoke={() => revoke.mutate(i.id)}
                      />
                    ))}
                  </ul>
                  <Pager
                    pager={invitePager}
                    shown={invitePage.data.items.length}
                    total={invitePage.data.total}
                    hasNext={!!invitePage.data.next_page_token}
                    onNext={() =>
                      invitePage.data?.next_page_token && invitePager.next(invitePage.data.next_page_token)
                    }
                    loading={invitePage.isFetching}
                    noun="invitations"
                  />
                </div>
              )}
            </section>

            <RolesInUse
              roles={allRoles}
              people={people}
              invites={invites}
              onCompare={() => setTab("roles")}
              onFilter={(id) => setView({ role: roleFilter === id ? "" : id })}
            />
          </div>
        </>
      )}

      {dialog?.kind === "invite" && (
        <InviteDialog roles={roles.data} presetOwner={dialog.owner} onClose={() => setDialog(null)} />
      )}
      {dialog?.kind === "manage" && (
        <ManageMemberDialog
          member={dialog.member}
          roles={roles.data}
          self={dialog.member.user.id === me.id}
          onRemove={() => setDialog({ kind: "remove", member: dialog.member })}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === "remove" && (
        <RemoveMemberDialog
          member={dialog.member}
          self={dialog.member.user.id === me.id}
          onClose={() => setDialog({ kind: "manage", member: dialog.member })}
          onRemoved={() => setDialog(null)}
        />
      )}
      {dialog?.kind === "only-owner" && (
        <OnlyOwnerDialog
          onTransfer={() => setDialog({ kind: "transfer" })}
          onAddOwner={() => setDialog({ kind: "invite", owner: true })}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === "transfer" && (
        <TransferOwnershipDialog
          members={people.filter((m) => m.user.id !== me.id)}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  );
}

/** "2 websites" — where someone's roles are narrowed; per role when they hold several. */
function scopeCaption(
  roles: { name: string; restrictions?: RoleRestrictions | null }[],
  names: ResourceNames,
): string {
  const narrowed = roles
    .map((r) => ({ name: r.name, summary: restrictionSummary(r.restrictions, names) }))
    .filter((r) => r.summary);
  if (roles.length === 1) return narrowed[0]?.summary ?? "";
  return narrowed.map((r) => `${r.name}: ${r.summary}`).join(" · ");
}

function RoleWithScope({
  roleKey,
  scope,
  children,
}: {
  roleKey: string | undefined;
  scope: string;
  children: ReactNode;
}) {
  if (!scope) return <RolePill roleKey={roleKey}>{children}</RolePill>;
  return (
    <span className="flex max-w-[45%] shrink-0 flex-col items-end gap-0.5">
      <RolePill roleKey={roleKey}>{children}</RolePill>
      <span className="max-w-full truncate text-[11px] text-fg-subtle" title={scope}>
        {scope}
      </span>
    </span>
  );
}

function InvitationRow({
  invitation: i,
  role,
  scope,
  canAct,
  resending,
  onResend,
  onRevoke,
}: {
  invitation: Invitation;
  role: Role | undefined;
  scope: string;
  canAct: boolean;
  resending: boolean;
  onResend: () => void;
  onRevoke: () => void;
}) {
  return (
    <li className="flex items-center gap-3 py-3">
      <InviteAvatar />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-fg">{i.invitee_name ?? i.email}</p>
        <p className="truncate text-xs text-fg-subtle">
          {i.invitee_name && `${i.email} · `}Invited {timeAgo(i.created_at)} · waiting to accept
        </p>
      </div>
      <RoleWithScope roleKey={role?.key} scope={scope}>
        {role?.name ?? "Member"}
      </RoleWithScope>
      <div className="flex items-center gap-1">
        <Button
          variant="secondary"
          size="sm"
          className="rounded-full"
          loading={resending}
          disabledReason={canAct ? undefined : missingPermissionReason("members:invite")}
          onClick={onResend}
        >
          Resend
        </Button>
        <Button
          unstyled
          onClick={onRevoke}
          aria-label={`Cancel the invitation to ${i.email}`}
          title={canAct ? "Cancel invitation" : undefined}
          disabledReason={canAct ? undefined : missingPermissionReason("members:invite")}
          className="rounded-full p-1.5 text-fg-subtle hover:bg-muted hover:text-fg"
        >
          <X className="size-4" />
        </Button>
      </div>
    </li>
  );
}

function RolesInUse({
  roles,
  people,
  invites,
  onCompare,
  onFilter,
}: {
  roles: Role[];
  people: Member[];
  invites: Invitation[];
  onCompare: () => void;
  onFilter: (roleId: string) => void;
}) {
  return (
    <section className={cx(panel, "px-5 py-4")}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-base font-semibold text-fg">
          Roles in use <HelpHint helpKey={HELP_KEYS.team.roles} label="team roles" />
        </h2>
        <Button
          unstyled
          type="button"
          onClick={onCompare}
          className="text-[13px] font-semibold text-brand-600 hover:underline"
        >
          See what each role can do →
        </Button>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {roles.map((r) => {
          const held = people.filter((m) => m.roles.some((x) => x.id === r.id)).length;
          const invited = invites.filter((i) => i.role_ids.includes(r.id)).length;
          const used = held + invited > 0;
          return (
            <Button
              unstyled
              key={r.id}
              type="button"
              title={describeRole(r)}
              onClick={() => onFilter(r.id)}
              className={cx(
                "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[12.5px]",
                used
                  ? "border-[#ecd9cf] bg-brand-50 font-semibold text-fg"
                  : "border-border bg-surface text-fg-subtle",
              )}
            >
              {r.name}
              <span className="font-normal text-fg-subtle">{held}</span>
              {invited > 0 && <span className="font-normal text-fg-subtle">· {invited} invited</span>}
            </Button>
          );
        })}
      </div>
      <p className="mt-4 text-xs text-fg-subtle">
        Role changes apply straight away. A business always keeps at least one Owner.
      </p>
    </section>
  );
}

function TransferBanner({
  invitation,
  canAct,
  busy,
  onResend,
  onCancel,
}: {
  invitation: Invitation;
  canAct: boolean;
  busy: boolean;
  onResend: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-[14px] border border-[#f0d28a] bg-[#fdf4dc] px-5 py-3.5">
      <Crown className="size-5 shrink-0 text-[#995c05]" aria-hidden />
      <p className="min-w-0 flex-1 text-sm text-[#5c4410]">
        <strong className="text-fg">
          Ownership offered to {invitation.invitee_name ?? invitation.email}.
        </strong>{" "}
        They become Owner when they accept, and the current Owner then becomes a Business Administrator.
      </p>
      <div className="flex gap-2">
        <Button
          variant="secondary"
          size="sm"
          loading={busy}
          disabledReason={canAct ? undefined : "Only the Owner can change an ownership offer."}
          onClick={onResend}
        >
          Resend
        </Button>
        <Button
          variant="ghost"
          size="sm"
          loading={busy}
          disabledReason={canAct ? undefined : "Only the Owner can change an ownership offer."}
          onClick={onCancel}
        >
          Cancel transfer
        </Button>
      </div>
    </div>
  );
}
